import { useCallback, useState, useRef, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { db } from "../config/firebase";
import { doc, collection, getDoc, updateDoc, setDoc } from "firebase/firestore";
import { getFunctions, httpsCallable } from "firebase/functions";
import { writeTeamActivity } from "../config/teamActivity";
import store from "../setup/redux/Store";

interface LocationState {
  projectID: string;
  userID: string;
}

interface UseStepFirestoreResult<T> {
  formData: T | null;
  loading: boolean;
  error: string | null;
  fetchFormData: () => Promise<void>;
  writeFormData: (data: Record<string, unknown>) => Promise<void>;
  updateProgress: (stepProgress: number) => Promise<void>;
  recalculateScore: () => Promise<void>;
  resetError: () => void;
  projectID: string;
  userID: string;
}

/**
 * Custom hook for managing Firestore operations for step forms
 * Extracts common Firestore logic used across all 24 step forms
 */
export function useStepFirestore<T = Record<string, unknown>>(
  stepNumber: number
): UseStepFirestoreResult<T> {
  const location = useLocation();
  const state = location.state as LocationState | null;
  const projectID = state?.projectID ?? "";
  const userID = state?.userID ?? "";

  const [formData, setFormData] = useState<T | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Track mounted state to prevent state updates after unmount
  const isMountedRef = useRef(true);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  // Lazy cache for teamId — undefined = not yet loaded, null = not a team project
  const teamIdRef = useRef<string | null | undefined>(undefined);

  // Load teamId once on mount for activity writing (non-critical)
  useEffect(() => {
    if (!userID || !projectID) return;
    getDoc(doc(db, "users", userID, "projects", projectID))
      .then((snap) => {
        teamIdRef.current = (snap.data()?.teamId as string) || null;
      })
      .catch(() => {
        teamIdRef.current = null;
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userID, projectID]);

  const stepKey = `step${stepNumber}`;

  /**
   * Reset error state
   */
  const resetError = useCallback(() => {
    setError(null);
  }, []);

  /**
   * Get reference to the step document in Firestore
   */
  const getStepRef = useCallback(() => {
    if (!userID || !projectID) {
      throw new Error("User ID and Project ID are required");
    }
    const userRef = doc(db, "users", userID);
    const projectRef = collection(userRef, "projects");
    const projectDocRef = doc(projectRef, projectID);
    const progressCollectionRef = collection(projectDocRef, "progress");
    return doc(progressCollectionRef, stepKey);
  }, [userID, projectID, stepKey]);

  /**
   * Get reference to the project document in Firestore
   */
  const getProjectRef = useCallback(() => {
    if (!userID || !projectID) {
      throw new Error("User ID and Project ID are required");
    }
    const userRef = doc(db, "users", userID);
    const projectRef = collection(userRef, "projects");
    return doc(projectRef, projectID);
  }, [userID, projectID]);

  /**
   * Fetch form data from Firestore
   */
  const fetchFormData = useCallback(async () => {
    if (!userID || !projectID) {
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const stepRef = getStepRef();
      const stepDoc = await getDoc(stepRef);

      // Check if still mounted before updating state
      if (!isMountedRef.current) return;

      const stepData = stepDoc.data();

      if (stepData?.form) {
        setFormData(stepData.form as T);
      }
    } catch (err) {
      // Only update error state if still mounted
      if (isMountedRef.current) {
        setError("Failed to load form data");
      }
    } finally {
      if (isMountedRef.current) {
        setLoading(false);
      }
    }
  }, [userID, projectID, getStepRef]);

  /**
   * Write form data to Firestore
   */
  const writeFormData = useCallback(
    async (data: Record<string, unknown>) => {
      if (!userID || !projectID) {
        throw new Error("User ID and Project ID are required");
      }

      setLoading(true);
      setError(null);

      try {
        const now = new Date().toISOString();
        const stepRef = getStepRef();
        await setDoc(
          stepRef,
          {
            form: data,
            updatedAt: now,
          },
          { merge: true }
        );
        // Stamp project doc with lastActiveAt so project list can show "Active X ago"
        const projectRef = getProjectRef();
        await setDoc(projectRef, { lastActiveAt: now }, { merge: true });

        // Write team activity (fire-and-forget — non-critical)
        if (teamIdRef.current) {
          const currentUser = store.getState().auth?.user;
          writeTeamActivity(teamIdRef.current, {
            type: "step_edited",
            actorUID: userID,
            actorEmail: currentUser?.email ?? "",
            projectID,
            stepNumber,
          });
        }
      } catch (err) {
        if (isMountedRef.current) {
          setError("Failed to save form data");
        }
        throw err;
      } finally {
        if (isMountedRef.current) {
          setLoading(false);
        }
      }
    },
    [userID, projectID, getStepRef, getProjectRef]
  );

  /**
   * Update step and project progress in Firestore.
   * projectProgress tracks the count of distinct steps that have been saved at least once.
   * It only increments when a step has no prior stepProgress value, preventing inflation
   * from repeated saves or auto-saves.
   */
  const updateProgress = useCallback(
    async (stepProgress: number) => {
      if (!userID || !projectID) {
        throw new Error("User ID and Project ID are required");
      }

      try {
        const projectRef = getProjectRef();
        const stepRef = getStepRef();

        // Check if this step has already been counted (stepProgress > 0 means first save happened)
        const stepDoc = await getDoc(stepRef);

        if (!isMountedRef.current) return;

        const stepData = stepDoc.data();
        const alreadyCounted = typeof stepData?.stepProgress === "number" && stepData.stepProgress > 0;

        // Update step progress (and record startedAt on first save)
        await updateDoc(stepRef, {
          stepProgress: Math.min(stepProgress, 100),
          ...(alreadyCounted ? {} : { startedAt: new Date().toISOString() }),
        });

        // Only increment project-level counter on first save of this step
        if (!alreadyCounted) {
          const projectDoc = await getDoc(projectRef);
          if (!isMountedRef.current) return;
          const currentProgress = projectDoc.data()?.projectProgress ?? 0;
          await updateDoc(projectRef, {
            projectProgress: currentProgress + 1,
          });
        }
      } catch {
        // Log progress update failures for debugging but don't break the form
        // The user can still continue even if progress tracking fails
        if (isMountedRef.current) {
          setError("Progress update failed, but your form data was saved.");
        }
      }
    },
    [userID, projectID, getProjectRef, getStepRef]
  );

  /**
   * Trigger score recalculation after step edits
   */
  const recalculateScore = useCallback(async () => {
    if (!projectID) return;
    try {
      const functions = getFunctions();
      const calculateBusinessScore = httpsCallable(functions, "calculateBusinessScore");
      await calculateBusinessScore({ projectId: projectID });
    } catch {
      // Score recalculation is non-critical; don't block the user
    }
  }, [projectID]);

  /**
   * Write form data and trigger score recalculation
   */
  const writeFormDataAndRecalculate = useCallback(
    async (data: Record<string, unknown>) => {
      await writeFormData(data);
      // Fire and forget score recalculation
      recalculateScore();
    },
    [writeFormData, recalculateScore]
  );

  return {
    formData,
    loading,
    error,
    fetchFormData,
    writeFormData: writeFormDataAndRecalculate,
    updateProgress,
    resetError,
    recalculateScore,
    projectID,
    userID,
  };
}
